import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Mail, MapPin } from "lucide-react";
import { Nav } from "@/components/landing/Nav";
import { Footer } from "@/components/landing/Footer";

const sections = [
  {
    heading: "Background",
    content: [
      { type: "p", text: "Ascend Property Group understands that your privacy is important to you and that you care about how your personal data is used. We respect and value the privacy of everyone who visits this website (“Our Site”) and will only collect and use personal data in ways that are described here, and in a way that is consistent with our obligations and your rights under the law." },
      { type: "p", text: "Please read this Privacy Policy carefully and ensure that you understand it. Your acceptance of this Privacy Policy is requested." },
    ],
  },
  {
    heading: "1. Definitions and Interpretation",
    content: [
      { type: "p", text: "In this Policy the following terms shall have the following meanings:" },
      {
        type: "deflist",
        items: [
          { term: "“Account”", def: "means an account required to access and/or use certain areas and features of Our Site;" },
          { term: "“Cookie”", def: "means a small text file placed on your computer or device by Our Site when you visit certain parts of Our Site and/or when you use certain features of Our Site. Details of the Cookies used by Our Site are set out in Part 14, below; and" },
          { term: "“Cookie Law”", def: "means the relevant parts of the Privacy and Electronic Communications (EC Directive) Regulations 2003." },
        ],
      },
    ],
  },
  {
    heading: "2. Information About Us",
    content: [
      { type: "p", text: "Our Site is owned and operated by Ascend Property Group, a Limited company registered in England under company number 14038279." },
      { type: "p", html: "<strong>Registered address:</strong> Unit 1 Mary Rose Mall, Frobisher Road, Beckton, United Kingdom, E6 5LX." },
      { type: "p", html: '<strong>Email address:</strong> <a class="link-gold" href="mailto:info@ascendpropertygroup.co.uk">info@ascendpropertygroup.co.uk</a>' },
    ],
  },
  {
    heading: "3. What Does This Policy Cover?",
    content: [
      { type: "p", text: "This Privacy Policy applies only to your use of Our Site. Our Site may contain links to other websites. Please note that we have no control over how your data is collected, stored, or used by other websites and we advise you to check the privacy policies of any such websites before providing any data to them." },
    ],
  },
  {
    heading: "4. What Is Personal Data?",
    content: [
      { type: "p", text: "Personal data is defined by the UK GDPR and the Data Protection Act 2018 (collectively, “the Data Protection Legislation”) as ‘any information relating to an identifiable person who can be directly or indirectly identified in particular by reference to an identifier’." },
      { type: "p", text: "Personal data is, in simpler terms, any information about you that enables you to be identified. Personal data covers obvious information such as your name and contact details, but it also covers less obvious information such as identification numbers, electronic location data, and other online identifiers." },
    ],
  },
  {
    heading: "5. What Are My Rights?",
    content: [
      { type: "p", text: "Under the Data Protection Legislation, you have the following rights, which we will always work to uphold:" },
      {
        type: "list",
        items: [
          "The right to be informed about our collection and use of your personal data. This Privacy Policy should tell you everything you need to know, but you can always contact us to find out more or to ask any questions using the details in Part 15.",
          "The right to access the personal data we hold about you. Part 13 will tell you how to do this.",
          "The right to have your personal data rectified if any of your personal data held by us is inaccurate or incomplete. Please contact us using the details in Part 15 to find out more.",
          "The right to be forgotten, i.e. the right to ask us to delete or otherwise dispose of any of your personal data that we hold. Please contact us using the details in Part 15 to find out more.",
          "The right to restrict (i.e. prevent) the processing of your personal data.",
          "The right to object to us using your personal data for a particular purpose or purposes.",
          "The right to withdraw consent. This means that, if we are relying on your consent as the legal basis for using your personal data, you are free to withdraw that consent at any time.",
          "The right to data portability. This means that, if you have provided personal data to us directly, we are using it with your consent or for the performance of a contract, and that data is processed using automated means, you can ask us for a copy of that personal data to re-use with another service or business in many cases.",
          "Rights relating to automated decision-making and profiling.",
        ],
      },
      { type: "p", text: "For more information about our use of your personal data or exercising your rights as outlined above, please contact us using the details provided in Part 15." },
      { type: "p", text: "It is important that your personal data is kept accurate and up-to-date. If any of the personal data we hold about you changes, please keep us informed as long as we have that data." },
      { type: "p", text: "Further information about your rights can also be obtained from the Information Commissioner’s Office or your local Citizens Advice Bureau." },
      { type: "p", text: "If you have any cause for complaint about our use of your personal data, you have the right to lodge a complaint with the Information Commissioner’s Office. We would welcome the opportunity to resolve your concerns ourselves, however, so please contact us first, using the details in Part 15." },
    ],
  },
  {
    heading: "6. What Data Do You Collect and How?",
    content: [
      { type: "p", text: "Depending upon your use of Our Site, we may collect and hold some or all of the personal data set out in the table below, using the methods also set out in the table. Please also see Part 14 for more information about our use of Cookies and similar technologies." },
      {
        type: "table",
        headers: ["Data Collected", "How We Collect the Data"],
        rows: [
          ["Identity information including name, title, date of birth, gender", "Online form, via conversation, or in person meeting"],
          ["Contact information including address, email address, telephone number", "Online form, via conversation, or in person meeting"],
        ],
      },
    ],
  },
  {
    heading: "7. How Do You Use My Personal Data?",
    content: [
      { type: "p", text: "Under the Data Protection Legislation, we must always have a lawful basis for using personal data. The following table describes how we may use your personal data, and our lawful bases for doing so:" },
      {
        type: "table",
        headers: ["What We Do", "What Data We Use", "Our Lawful Basis"],
        rows: [
          ["Registering you on Our Site.", "Name, Email, Contact Number.", "Consent provided by you."],
          ["Providing and managing your Account.", "Notes relating to our discussions and your needs.", "Legitimate interest in providing the best service."],
          ["Administering our business.", "Notes relating to our discussions and your needs.", "Legitimate interest in providing the best service."],
          ["Supplying our services to you.", "Notes relating to our discussions and your needs.", "Legitimate interest in providing the best service."],
          ["Managing payments for our services.", "Payment for our services.", "Consent provided by you. We will only process payments following consent from you."],
          ["Communicating with you.", "Notes relating to our discussions and your needs.", "Legitimate interest in providing the best service."],
        ],
      },
      { type: "p", text: "We will only use your personal data for the purpose(s) for which it was originally collected unless we reasonably believe that another purpose is compatible with that or those original purpose(s) and need to use your personal data for that purpose. If we do use your personal data in this way and you wish us to explain how the new purpose is compatible with the original, please contact us using the details in Part 15." },
      { type: "p", text: "If we need to use your personal data for a purpose that is unrelated to, or incompatible with, the purpose(s) for which it was originally collected, we will inform you and explain the legal basis which allows us to do so." },
      { type: "p", text: "In some circumstances, where permitted or required by law, we may process your personal data without your knowledge or consent. This will only be done within the bounds of the Data Protection Legislation and your legal rights." },
    ],
  },
  {
    heading: "8. How Long Will You Keep My Personal Data?",
    content: [
      { type: "p", text: "We will not keep your personal data for any longer than is necessary in light of the reason(s) for which it was first collected. Your personal data will therefore be kept for the following periods (or, where there is no fixed period, the following factors will be used to determine how long it is kept):" },
      {
        type: "table",
        headers: ["Type of Data", "How Long We Keep It"],
        rows: [
          ["Identity information including name, title, date of birth and gender.", "For as long as you are registered with our database and for a period of up to 28 days following a request for deletion."],
          ["Contact information including address, email address and telephone number.", "For as long as you are registered with our database and for a period of up to 28 days following a request for deletion."],
          ["Payment information including card details and bank account numbers.", "For as long as you are registered with our database and for a period of up to 28 days following a request for deletion."],
        ],
      },
    ],
  },
  {
    heading: "9. How and Where Do You Store or Transfer My Personal Data?",
    content: [
      { type: "p", html: 'We will only store or transfer personal data in or to countries that are deemed to provide an adequate level of protection for personal data. For further information about adequacy decisions and adequacy regulations, please refer to the <a class="link-gold" href="https://ico.org.uk/" target="_blank" rel="noopener noreferrer">Information Commissioner’s Office</a>.' },
      { type: "p", text: "Please contact us using the details below in Part 15 for further information about the particular data protection safeguards used by us when transferring your personal data to a third country." },
      { type: "p", text: "The security of your personal data is essential to us, and to protect your data, we take a number of important measures, including the following:" },
      {
        type: "list",
        items: [
          "Limiting access to your personal data to those employees, agents, contractors, and other third parties with a legitimate need to know and ensuring that they are subject to duties of confidentiality;",
          "Procedures for dealing with data breaches (the accidental or unlawful destruction, loss, alteration, unauthorised disclosure of, or access to, your personal data) including notifying you and/or the Information Commissioner’s Office where we are legally required to do so.",
        ],
      },
    ],
  },
  {
    heading: "10. Do You Share My Personal Data?",
    content: [
      { type: "p", text: "We will not share any of your personal data with any third parties for any purposes, subject to the following exceptions." },
      { type: "p", text: "If we sell, transfer, or merge parts of our business or assets, your personal data may be transferred to a third party. Any new owner of our business may continue to use your personal data in the same way(s) that we have used it, as specified in this Privacy Policy." },
      { type: "p", text: "In some limited circumstances, we may be legally required to share certain personal data, which might include yours, if we are involved in legal proceedings or complying with legal obligations, a court order, or the instructions of a government authority." },
    ],
  },
  {
    heading: "11. How Can I Control My Personal Data?",
    content: [
      {
        type: "list",
        items: [
          "In addition to your rights under the Data Protection Legislation, set out in Part 5, when you submit personal data via Our Site, you may be given options to restrict our use of your personal data. In particular, we aim to give you strong controls on our use of your data for direct marketing purposes (including the ability to opt-out of receiving emails from us which you may do by unsubscribing using the links provided in our emails and at the point of providing your details).",
          "You may also wish to sign up to one or more of the preference services operating in the UK: The Telephone Preference Service (“the TPS”), the Corporate Telephone Preference Service (“the CTPS”), and the Mailing Preference Service (“the MPS”). These may help to prevent you receiving unsolicited marketing. Please note, however, that these services will not prevent you from receiving marketing communications that you have consented to receiving.",
        ],
      },
    ],
  },
  {
    heading: "12. Can I Withhold Information?",
    content: [
      { type: "p", text: "You may access Our Site without providing any personal data at all." },
      { type: "p", text: "You may restrict our use of Cookies. For more information, see Part 14." },
    ],
  },
  {
    heading: "13. How Can I Access My Personal Data?",
    content: [
      { type: "p", text: "If you want to know what personal data we have about you, you can ask us for details of that personal data and for a copy of it (where any such personal data is held). This is known as a “subject access request”." },
      { type: "p", text: "All subject access requests should be made in writing and sent to the email or postal addresses shown in Part 15." },
      { type: "p", text: "There is not normally any charge for a subject access request. If your request is ‘manifestly unfounded or excessive’ (for example, if you make repetitive requests) a fee may be charged to cover our administrative costs in responding." },
      { type: "p", text: "We will respond to your subject access request within one month of receiving it. Normally, we aim to provide a complete response, including a copy of your personal data within that time. In some cases, however, particularly if your request is more complex, more time may be required up to a maximum of three months from the date we receive your request. You will be kept fully informed of our progress." },
    ],
  },
  {
    heading: "14. How Do You Use Cookies?",
    content: [
      { type: "p", text: "Our Site may place and access certain first-party Cookies on your computer or device. First-party Cookies are those placed directly by us and are used only by us. We use Cookies to facilitate and improve your experience of Our Site and to provide and improve our services." },
    ],
  },
  {
    heading: "15. How Do I Contact You?",
    content: [
      { type: "p", text: "To contact us about anything to do with your personal data and data protection, including to make a subject access request, please use the following details:" },
      { type: "p", html: '<strong>Email address:</strong> <a class="link-gold" href="mailto:info@ascendpropertygroup.co.uk">info@ascendpropertygroup.co.uk</a>' },
      { type: "p", html: "<strong>Postal Address:</strong> Unit 1 Mary Rose Mall, Frobisher Road, Beckton, United Kingdom, E6 5LX." },
    ],
  },
  {
    heading: "16. Changes to this Privacy Policy",
    content: [
      { type: "p", text: "We may change this Privacy Notice from time to time. This may be necessary, for example, if the law changes, or if we change our business in a way that affects personal data protection." },
      { type: "p", text: "Any changes will be immediately posted on Our Site and you will be deemed to have accepted the terms of the Privacy Policy on your first use of Our Site following the alterations. We recommend that you check this page regularly to keep up-to-date. This Privacy Policy was last updated on 7 June 2025." },
    ],
  },
];

function Block({ block }) {
  if (block.type === "p") {
    if (block.html) {
      return (
        <p
          className="text-[0.98rem] leading-[1.75] text-[var(--navy)]/85 mb-4"
          dangerouslySetInnerHTML={{ __html: block.html }}
        />
      );
    }
    return <p className="text-[0.98rem] leading-[1.75] text-[var(--navy)]/85 mb-4">{block.text}</p>;
  }
  if (block.type === "list") {
    return (
      <ul className="space-y-3 mb-5 mt-2">
        {block.items.map((item, i) => (
          <li key={i} className="flex items-start gap-3 text-[0.98rem] leading-[1.7] text-[var(--navy)]/85">
            <span className="mt-[10px] w-1.5 h-1.5 rounded-full bg-[var(--gold-deep)] shrink-0" />
            <span>{item}</span>
          </li>
        ))}
      </ul>
    );
  }
  if (block.type === "deflist") {
    return (
      <dl className="mb-5 mt-2 divide-y divide-[rgba(201,168,76,0.25)] border-y border-[rgba(201,168,76,0.25)]">
        {block.items.map((it, i) => (
          <div key={i} className="grid sm:grid-cols-[160px_1fr] gap-4 py-4">
            <dt className="font-display text-[var(--gold-deep)] text-[1.05rem]">{it.term}</dt>
            <dd className="text-[0.95rem] leading-[1.7] text-[var(--navy)]/85">{it.def}</dd>
          </div>
        ))}
      </dl>
    );
  }
  if (block.type === "table") {
    return (
      <div className="mb-5 mt-2 overflow-x-auto border border-[rgba(201,168,76,0.3)]">
        <table className="w-full text-left text-[0.92rem]">
          <thead className="bg-[var(--navy)] text-[var(--ivory)]">
            <tr>
              {block.headers.map((h, i) => (
                <th key={i} className="px-4 py-3 font-display text-[var(--gold)] tracking-wide text-[0.95rem] border-r border-[rgba(201,168,76,0.2)] last:border-r-0">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {block.rows.map((row, i) => (
              <tr key={i} className="border-t border-[rgba(201,168,76,0.18)] align-top">
                {row.map((c, j) => (
                  <td key={j} className="px-4 py-3 text-[var(--navy)]/85 border-r border-[rgba(201,168,76,0.12)] last:border-r-0 leading-[1.65]">
                    {c}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }
  return null;
}

export default function PrivacyPolicy() {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
    document.title = "Privacy & Cookies Policy — Ascend Property Group";
  }, []);

  return (
    <div data-testid="privacy-page" className="min-h-screen bg-[var(--ivory)] text-[var(--ink)]">
      <Nav scrolled={true} />

      {/* Header */}
      <section className="bg-[var(--navy)] text-[var(--ivory)] grain-overlay pt-36 pb-20">
        <div className="max-w-[920px] mx-auto px-6 lg:px-10">
          <div className="reveal in">
            <Link
              to="/"
              data-testid="back-to-home"
              className="inline-flex items-center gap-2 text-[0.78rem] tracking-[0.2em] uppercase text-[var(--gold)] hover:text-[var(--ivory)] transition-colors mb-8"
            >
              <ArrowLeft size={14} />
              Back to home
            </Link>
            <div className="flex items-center gap-3 mb-5">
              <span className="h-px w-10 bg-[var(--gold)]" />
              <span className="text-[0.7rem] tracking-[0.32em] uppercase text-[var(--gold)]">
                Legal
              </span>
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-[3.75rem] leading-[1.05]">
              Privacy &amp;{" "}
              <span className="italic text-[var(--gold)]">Cookies Policy.</span>
            </h1>
            <p className="mt-6 text-[var(--ivory)]/70 max-w-2xl leading-relaxed">
              How Ascend Property Group collects, uses, stores and protects your
              personal data. Last updated 7 June 2025.
            </p>
          </div>
        </div>
      </section>

      {/* Body */}
      <section className="bg-[var(--ivory)] py-20 lg:py-24">
        <div className="max-w-[920px] mx-auto px-6 lg:px-10">
          <article className="prose-policy">
            {sections.map((s, i) => (
              <div key={i} className="mb-12" data-testid={`policy-section-${i}`}>
                <h2 className="font-display text-[1.7rem] sm:text-[2rem] text-[var(--navy)] leading-[1.15] mb-5">
                  {s.heading}
                </h2>
                {s.content.map((b, j) => (
                  <Block key={j} block={b} />
                ))}
              </div>
            ))}

            {/* Contact summary card */}
            <div className="mt-16 p-8 sm:p-10 border border-[rgba(201,168,76,0.45)] bg-white relative">
              <span className="absolute top-0 left-0 right-0 h-[2px] bg-[var(--gold)]" />
              <p className="text-[0.7rem] tracking-[0.28em] uppercase text-[var(--gold-deep)] mb-4">
                Get in touch
              </p>
              <p className="font-display text-[1.5rem] text-[var(--navy)] leading-tight mb-6">
                Questions about your data?
              </p>
              <div className="space-y-3 text-[var(--navy)]/85">
                <a
                  href="mailto:info@ascendpropertygroup.co.uk"
                  className="link-gold inline-flex items-center gap-3"
                >
                  <Mail size={16} className="text-[var(--gold-deep)]" />
                  info@ascendpropertygroup.co.uk
                </a>
                <p className="flex items-start gap-3 text-[0.95rem]">
                  <MapPin size={16} className="text-[var(--gold-deep)] mt-1 shrink-0" />
                  Unit 1 Mary Rose Mall, Frobisher Road, Beckton, United Kingdom, E6 5LX
                </p>
              </div>
            </div>
          </article>
        </div>
      </section>

      <Footer />
    </div>
  );
}
